import { memo } from 'react'
import { useTranslation } from 'next-i18next'

import FilePreviewContent from '~/components/FilePreviewContent'
import { ButtonPrimary } from '~/components/Buttons'
import { PreviewTopic } from '~/types/theory'
import { getIconName } from '~/encyclopedia/utils/encyclopedia.utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '60vw' }]

interface Props {
  fileTopic: PreviewTopic
  showImage: boolean
}

const FilePreview = ({ fileTopic, showImage }: Props): React.ReactElement => {
  const { t } = useTranslation('encyclopedia')
  const icon = getIconName(fileTopic.path)
  const previewAltText = t('previewAltPrefix', { title: fileTopic.h1 })
  return (
    <div className="isolate relative max-w-sm min-h mx-auto md:m-0 rounded bg-white shadow-lg">
      <article className="p-4 flex flex-col h-full">
        <header className="flex self-center w-4/5">
          <FilePreviewTitle title={fileTopic.h1} />
          <FilePreviewIcon icon={icon} />
        </header>
        <div className="px-4 py-2 h-full flex flex-col items-center justify-between">
          {fileTopic.smallPreviewUrl && (
            <FilePreviewContent alt={previewAltText} url={showImage ? fileTopic.smallPreviewUrl : undefined} />
          )}
          <ButtonPrimary className="mt-auto z-10" href={fileTopic.path} isWide link="internal" size="small">
            {t('readHere')}
          </ButtonPrimary>
        </div>
      </article>
    </div>
  )
}

function FilePreviewIcon({ icon }: { icon?: { src: string; alt: string } }): React.ReactElement {
  if (icon?.src) {
    return (
      <ResponsiveImage
        alt={icon?.alt}
        imageClassName="absolute top-0 right-0 m-4 w-6 self-end z-30"
        mediaQuery={mediaQuery}
        srcKey={icon.src}
      />
    )
  }
  return <></>
}

function FilePreviewTitle({ title }: { title: string }): React.ReactElement {
  return (
    <div className="relative w-full top-0  z-20">
      <div className="px-4 pb-1 bg-white/75 backdrop-filter backdrop-blur-sm">
        <h4 className="font-semibold text-md text-purple text-center">{title}</h4>
      </div>
    </div>
  )
}

export default memo(FilePreview)
